/**
 * modelSatellites — turn a laid-out flow into that flow PLUS one model disc per
 * model-running node, joined by a dashed connector.
 *
 * Pure: takes nodes + the state inputs, returns new nodes/edges. The disc's
 * appearance comes entirely from `nodeModelState`, so the graph, the trigger
 * gate and the sub-agent readiness check can never disagree about whether a
 * node is ready.
 */
import { nodeCfgOf, nodeModelState } from '../../../utils/nodeModel';

export const MODEL_NODE_PREFIX = '__model__';

// Where the disc sits relative to its node. Below-RIGHT: the node's own caption
// (124px wide, centered) owns the space directly beneath it, and the flow edge
// leaves the node's right side at icon-center height — well above this. The
// layer channel is widened to fit (see MODEL_DISC_LAYER_SPACING).
//
// The connector leaves the node's `bottom` handle, which CSS pins to the icon
// box's underside (top: 83px), NOT the bottom of the node element — so it
// starts at the box, above the caption.
const DISC_DX = 104;
const DISC_DY = 92;

/**
 * @param {object[]} nodes      laid-out React Flow nodes
 * @param {object[]} edges      laid-out React Flow edges
 * @param {object}   graph      the backend graph (for nodeConfigs)
 * @param {object}   ctx        { overrides, agentEnvKeys, projectSecrets,
 *                              projectVendor, modelBlocks, flagged, onPick }
 * @returns {{nodes: object[], edges: object[]}}
 */
export function withModelSatellites(nodes, edges, graph, ctx) {
  if (!Array.isArray(nodes) || nodes.length === 0) return { nodes, edges };
  const {
    overrides, agentEnvKeys, projectSecrets, projectVendor, modelBlocks, flagged, onPick,
    agentVendor,
  } = ctx || {};
  // Node ids the Trigger check pointed at — their discs get the warm ring so
  // the scroll lands on something findable.
  const flaggedSet = flagged instanceof Set ? flagged : new Set(flagged || []);

  const discNodes = [];
  const discEdges = [];

  for (const n of nodes) {
    if (!n?.data?.runsModel) continue;
    const cfg = nodeCfgOf(graph, n.id);
    const state = nodeModelState({
      cfg,
      nodeId: n.id,
      override: overrides?.[n.id],
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
      agentVendor,
      overrides,
    });
    const id = `${MODEL_NODE_PREFIX}${n.id}`;
    discNodes.push({
      id,
      type: 'modelNode',
      draggable: false,
      selectable: false,
      position: { x: (n.position?.x || 0) + DISC_DX, y: (n.position?.y || 0) + DISC_DY },
      data: {
        state,
        flagged: flaggedSet.has(n.id),
        tooltip: state.kind !== 'locked'
          ? `Model for ${n.data?.label || n.id}`
          : state.missingKey
            ? `${n.data?.label || n.id} runs a model the template fixes, but there's no ${state.vendor} key — click to add one.`
            : `${n.data?.label || n.id} runs a model the template fixes — the key is editable, the model isn't.`,
        // The disc ALWAYS owns its click, even when it opens nothing. Leaving
        // the handler off doesn't make the click inert — it makes the editor
        // fall through to the generic branch and open the node-config modal
        // for the disc's SYNTHETIC id, which has no config: a blank
        // "Node — Configuration" panel over a locked model.
        //
        // A LOCKED node is still clickable when we know its vendor: the
        // template chose the model, not the credential, so the operator must
        // be able to supply the key. The picker opens with the model read-only
        // and only the key editable. With no resolvable vendor there is
        // genuinely nothing to ask for, so it stays inert.
        onNodeClick: !onPick || (state.kind === 'locked' && !state.vendor)
          ? () => {}
          : () => onPick(n.id, n.data?.label || n.id, state),
      },
    });
    discEdges.push({
      id: `e${id}`,
      source: n.id,
      sourceHandle: 'bottom',
      target: id,
      targetHandle: 'disc',
      // Orthogonal elbow, like every other edge on this canvas. A free bezier
      // between a DOWN-facing source and a RIGHT-facing target swings wide and
      // low before doubling back, which reads as a disconnected stub floating
      // near the disc rather than a line joining it to its node.
      type: 'smoothstep',
      pathOptions: { borderRadius: 12 },
      focusable: false,
      selectable: false,
      style: {
        stroke: (state.kind === 'nokey' || state.missingKey)
          ? '#d9534f' : 'rgba(190, 190, 190, 0.85)',
        strokeWidth: 1.5,
        strokeDasharray: '5 4',
      },
    });
  }

  if (discNodes.length === 0) return { nodes, edges };
  return { nodes: [...nodes, ...discNodes], edges: [...edges, ...discEdges] };
}
